import React, { useContext, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { SettingsContext } from '../contexts/SettingsContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import conversationService from '../services/conversationService';
import ConversationHistory from './ConversationHistory';
import LoadingSpinner from './LoadingSpinner';
import Toast from './Toast';

const ConversationsContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: var(--space-3) var(--space-4);
  margin-bottom: var(--space-4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-lg);
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-color);
  font-family: var(--font-family);
  font-weight: 400;
  font-size: var(--text-base);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);

  &:focus {
    outline: none;
    border-color: rgba(74, 144, 226, 0.3);
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.05), 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  &:hover:not(:focus) {
    border-color: rgba(74, 144, 226, 0.2);
  }

  &::placeholder {
    color: var(--text-secondary);
    opacity: 0.7;
  }
`;

const NewConversationButton = styled.button`
  width: 100%;
  padding: var(--space-3) var(--space-5);
  background: linear-gradient(135deg, rgba(74, 144, 226, 0.3) 0%, rgba(74, 144, 226, 0.2) 100%);
  color: white;
  border: none;
  border-radius: var(--radius-lg);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: var(--space-4);
  font-family: var(--font-family);
  font-weight: 400;
  font-size: var(--text-base);
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(90, 99, 230, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 50%);
    border-radius: inherit;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    background: linear-gradient(135deg, var(--primary-700) 0%, var(--primary-800) 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(90, 99, 230, 0.4);

    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(90, 99, 230, 0.3);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }

  svg {
    margin-left: var(--space-2);
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-600);
  margin-bottom: var(--space-4);
  padding: var(--space-3);
  background-color: var(--error-50);
  border-radius: var(--radius-lg);
  border: 1px solid var(--error-200);
  font-size: var(--text-sm);
  font-weight: 500;
  font-family: var(--font-heading-regular);
`;

function Conversations({ userId, projectId, selectedFolder, onConversationSelect }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [conversations, setConversations] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [toast, setToast] = useState(null);

  const [serviceInitialized, setServiceInitialized] = useState(false);
  const [initializingService, setInitializingService] = useState(false);

  // Helper function for showing toasts
  const showToast = (message, type) => {
    setToast({ message, type });
  };

  // Initialize the conversation service with userId
  useEffect(() => {
    const initializeService = async () => {
      if (userId && !serviceInitialized && !initializingService) {
        setInitializingService(true);
        try {
          console.log('[Conversations] Initializing service for user:', userId);
          await conversationService.initialize(userId);
          setServiceInitialized(true);
          console.log('[Conversations] Service initialized successfully');
        } catch (error) {
          console.error('[Conversations] Error initializing conversation service:', error);
          showToast('Failed to initialize conversation service', 'error');
          // Set as initialized anyway to allow offline mode
          setServiceInitialized(true);
        } finally {
          setInitializingService(false);
        }
      }
    };
    initializeService();
  }, [userId, serviceInitialized, initializingService]);

  useEffect(() => {
    if (userId && serviceInitialized) {
      console.log('[Conversations] Fetching conversations, projectId:', projectId, 'folder:', selectedFolder?.name);
      fetchConversations();
    }
  }, [userId, projectId, selectedFolder, serviceInitialized]);

  const fetchConversations = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('[Conversations] Starting fetchConversations, projectId:', projectId);

      // Load all conversations from the service (handles both localStorage and Firestore)
      const allConversations = await conversationService.loadConversations();
      console.log('[Conversations] Loaded conversations:', allConversations.length, allConversations);

      // Filter conversations by project and folder
      let filteredConversations = allConversations;

      // Filter by project if selected
      if (projectId) {
        filteredConversations = filteredConversations.filter(conv => conv.projectId === projectId);
        console.log(`[Conversations] Filtered to project ${projectId}:`, filteredConversations.length);
      }

      // Filter by folder if selected
      if (selectedFolder) {
        filteredConversations = filteredConversations.filter(conv => conv.folderId === selectedFolder.id);
        console.log(`[Conversations] Filtered to folder ${selectedFolder.name}:`, filteredConversations.length);
      } else {
        // If no folder is selected, show conversations without a folder (root level)
        // Only apply this filter if we want to show root-level conversations
        // For now, show all conversations when no folder is selected
        console.log('[Conversations] No folder filter applied');
      }

      // Sort by updatedAt or createdAt (newest first)
      filteredConversations.sort((a, b) => {
        const dateA = a.updatedAt || a.createdAt || 0;
        const dateB = b.updatedAt || b.createdAt || 0;
        return dateB - dateA; // Descending order (newest first)
      });

      setConversations(filteredConversations);

      // Log if no conversations found
      if (filteredConversations.length === 0) {
        console.log('[Conversations] No conversations found. This could mean:');
        console.log('1. User has no conversations yet');
        console.log('2. Conversations are not loading from storage/Firestore');
        console.log('3. All conversations are filtered out by projectId');

        if (projectId && allConversations.length > 0) {
          console.log(`[Conversations] Note: Found ${allConversations.length} total conversations, but none match projectId: ${projectId}`);
        }
      }
    } catch (error) {
      console.error('[Conversations] Error fetching conversations:', error);
      setError('Failed to load conversations. Please try again.');
      showToast('Failed to load conversations. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewConversation = async () => {
    if (!userId || !projectId) {
      showToast('Please select a project before creating a new conversation.', 'error');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const conversationData = {
        projectId: projectId,
        title: 'New Conversation',
        messages: [],  // Initialize with empty messages array
        folderId: selectedFolder?.id || null,  // Include folder ID if folder is selected
        createdAt: Date.now(),
        updatedAt: Date.now()
      };

      // Save the new conversation using the service
      const savedConversation = await conversationService.saveConversation(conversationData);

      if (onConversationSelect) {
        onConversationSelect(savedConversation);
      }

      // Refresh the conversations list
      await fetchConversations();
      showToast('New conversation created successfully', 'success');
    } catch (error) {
      console.error('Error creating new conversation:', error);
      showToast('Failed to create a new conversation. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectConversation = async (conversation) => {
    console.log('[Conversations] handleSelectConversation called with:', conversation);
    setSelectedConversation(conversation);

    // Just pass the conversation to the parent component (App.js)
    // which will handle displaying it in ChatWindowEnhanced
    if (onConversationSelect) {
      console.log('[Conversations] Calling onConversationSelect with conversation:', conversation);
      onConversationSelect(conversation);
    } else {
      console.warn('[Conversations] No onConversationSelect callback provided!');
    }
  };

  return (
    <ConversationsContainer>
      <SearchInput
        placeholder="Search conversations..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {initializingService ? (
        <div style={{
          padding: 'var(--space-4)',
          textAlign: 'center',
          color: 'var(--text-secondary)'
        }}>
          <LoadingSpinner />
          <p style={{ marginTop: 'var(--space-2)' }}>Initializing conversation service...</p>
        </div>
      ) : isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          {conversations.length === 0 ? (
            <div style={{
              padding: 'var(--space-6)',
              textAlign: 'center',
              color: 'var(--text-secondary)',
              backgroundColor: 'var(--surface-color)',
              borderRadius: 'var(--radius-lg)',
              border: '1px dashed var(--border-color-light)',
              marginBottom: 'var(--space-4)'
            }}>
              <FontAwesomeIcon
                icon={faPlus}
                style={{
                  fontSize: '2rem',
                  marginBottom: 'var(--space-3)',
                  opacity: 0.5
                }}
              />
              <p style={{ margin: 0, fontWeight: 500 }}>
                No conversations yet
              </p>
              <p style={{
                margin: 'var(--space-2) 0 0 0',
                fontSize: 'var(--text-sm)',
                opacity: 0.8
              }}>
                {projectId
                  ? 'Create your first conversation for this project'
                  : 'Select a project or create a new conversation to get started'}
              </p>
            </div>
          ) : (
            <ConversationHistory
              conversations={conversations}
              onSelectConversation={handleSelectConversation}
              currentConversationId={selectedConversation?.id}
              onConversationDeleted={async (conversationId) => {
                try {
                  await conversationService.deleteConversation(conversationId);
                  await fetchConversations();
                  showToast('Conversation deleted successfully', 'success');
                } catch (error) {
                  console.error('Error deleting conversation:', error);
                  showToast('Failed to delete conversation', 'error');
                }
              }}
            />
          )}
        </>
      )}
      <NewConversationButton onClick={handleNewConversation} disabled={isLoading}>
        <FontAwesomeIcon icon={faPlus} />
        New Conversation
      </NewConversationButton>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </ConversationsContainer>
  );
}

export default Conversations;